// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import { ResponsiveTable } from "../mobile/ResponsiveTable";
import React, { useEffect, useState } from "react";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Globe, Loader2, Save, BarChart3 } from "lucide-react";
import { useAiModels } from "../../lib/aiModels";

export const SOURCE_META = {
  osm: ["OSM 地理编码", "OSM geocoding", "photon.komoot.io / nominatim.openstreetmap.org"],
  assessor: ["洛杉矶县评估官", "LA County Assessor", "portal.assessor.lacounty.gov"],
  web: ["Gemini 联网搜索", "Gemini web search", "Google Search grounding"],
};

export function IntakeResearchSources({ api, zh }) {
  const aiModels = useAiModels();
  const [s, setS] = useState(null);
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showMonths, setShowMonths] = useState(false);
  const load = () => {
    api.get("/settings/intake-research").then(({ data }) => { setS(data); setContact(data.osmContact || ""); }).catch(() => {});
    api.get("/settings/intake-research/usage", { params: { months: 3 } }).then(({ data }) => setUsage(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const toggle = async (k, v) => {
    setBusy(true);
    try { const { data } = await api.put("/settings/intake-research", { [k]: v }); setS(data); toast.success(zh ? `${SOURCE_META[k][0]} 已${v ? "开启" : "关闭"}` : `${SOURCE_META[k][1]} ${v ? "on" : "off"}`); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setBusy(false);
  };
  const saveContact = async () => {
    setBusy(true);
    try { const { data } = await api.put("/settings/intake-research", { osmContact: contact.trim() }); setS(data); setContact(data.osmContact || ""); toast.success(zh ? "联系邮箱已保存" : "Contact saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setBusy(false);
  };
  if (!s) return null;
  const onCount = ["osm", "assessor", "web"].filter((k) => s[k]).length;
  return (
    <div className="rounded-xl border border-sky-100 bg-sky-50/40 px-4 py-3 text-xs space-y-2" data-testid="research-sources" data-on-count={onCount}>
      <div className="flex items-center gap-2 flex-wrap">
        <Globe className="h-3.5 w-3.5 text-sky-700" /><b className="text-sky-800">{zh ? "智能填表 · 地址联网检索来源" : "Intake address research sources"}</b>
        <span className="text-slate-500">{zh ? `启用 ${onCount}/3 · 仅管理员可改 · 关闭的来源不再对外发送地址` : `${onCount}/3 on · admin only`}</span>
        {s.updatedAt && <span className="text-slate-400 ml-auto" data-testid="research-sources-updated">{s.updatedBy} · {new Date(s.updatedAt).toLocaleString("zh-CN", { hour12: false })}</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {["osm", "assessor", "web"].map((k) => (
          <label key={k} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 bg-white ${s[k] ? "border-sky-200" : "border-slate-200 opacity-70"}`} data-testid={`research-source-${k}`} data-on={s[k] ? 1 : 0}>
            <span><b className="text-slate-800">{zh ? SOURCE_META[k][0] : SOURCE_META[k][1]}</b><br /><span className="text-[10px] text-slate-400 font-mono">{k === "web" && aiModels.search ? `${aiModels.search} · ${SOURCE_META[k][2]}` : SOURCE_META[k][2]}</span></span>
            <Switch checked={!!s[k]} disabled={busy} onCheckedChange={(v) => toggle(k, v)} data-testid={`research-source-${k}-switch`} />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-slate-600">{zh ? "OSM 请求头联系邮箱（服务方政策要求，不外显不发信）" : "OSM User-Agent contact email"}:</span>
        <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder={zh ? "未填写 → 请求头不带联系方式" : "empty → no contact in UA"} className="h-7 text-xs w-64 bg-white" data-testid="research-contact-input" />
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || (contact.trim() === (s.osmContact || ""))} onClick={saveContact} data-testid="research-contact-save-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />{zh ? "保存" : "Save"}</>}
        </Button>
        <span className="text-[11px] text-slate-400" data-testid="research-contact-state">{s.osmContact ? (zh ? "已设置" : "set") : (zh ? "未设置" : "not set")}</span>
      </div>
      {usage && (
        <div className="rounded-lg bg-white border border-slate-100 px-3 py-2 space-y-1" data-testid="research-usage" data-month={usage.current.month} data-total={usage.current.total}>
          <div className="flex items-center gap-2 flex-wrap">
            <BarChart3 className="h-3.5 w-3.5 text-sky-700" />
            <b className="text-slate-800">{zh ? "本月用量" : "This month"}</b>
            <span className="text-slate-700" data-testid="research-usage-current">
              OSM <b>{usage.current.osm}</b> · {zh ? "评估官" : "Assessor"} <b>{usage.current.assessor}</b> · Gemini <b>{usage.current.web}</b> · {zh ? "共" : "total"} <b>{usage.current.total}</b> {zh ? "次" : ""}
              {usage.current.avgMs != null && <span className="text-slate-400"> · {zh ? "平均" : "avg"} {(usage.current.avgMs / 1000).toFixed(1)}s</span>}
            </span>
            <span className="text-[10px] text-slate-400">{zh ? "按「已发送请求」计，不含地址；自本功能上线起统计" : "counts requests sent; no addresses stored"}</span>
            <button type="button" onClick={() => setShowMonths((v) => !v)} className="max-md:min-h-11 max-md:min-w-11 ml-auto text-[11px] text-sky-700 underline decoration-dotted" data-testid="research-usage-toggle">{showMonths ? (zh ? "收起" : "hide") : (zh ? "近 3 个月" : "last 3 months")}</button>
          </div>
          {showMonths && (
            <ResponsiveTable className="w-full text-[11px]" data-testid="research-usage-table">
              <thead><tr className="text-slate-400"><th className="text-left font-normal">{zh ? "月份" : "Month"}</th><th className="text-right font-normal">OSM</th><th className="text-right font-normal">{zh ? "评估官" : "Assessor"}</th><th className="text-right font-normal">Gemini</th><th className="text-right font-normal">{zh ? "共" : "Total"}</th><th className="text-right font-normal">{zh ? "失败" : "Errors"}</th></tr></thead>
              <tbody>{usage.months.map((m) => (
                <tr key={m.month} className="text-slate-700" data-testid={`research-usage-row-${m.month}`}><td>{m.month}</td><td className="text-right">{m.osm}</td><td className="text-right">{m.assessor}</td><td className="text-right">{m.web}</td><td className="text-right font-semibold">{m.total}</td><td className="text-right">{m.errors}</td></tr>
              ))}</tbody>
            </ResponsiveTable>
          )}
        </div>
      )}
    </div>
  );
}
